import clsx from 'clsx';
import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroller';
import { SearchResultAssetIcons } from '@/assetGroupEditor/SearchResultAssetIcons.molecule';
import { ErrorWithBackground } from '@/core/ErrorWithBackground.atom';
import { IconWithSpinner } from '@/core/IconWithSpinner.atom';
import {
  sqItemTableEditorStore,
  sqLayoutStore,
  sqLicenseManagementStore,
  sqScalingTableEditorStore,
  sqTableBuilderStore,
  sqWorkbenchStore,
  sqWorkbookStore,
} from '@/core/core.stores';
import { useFlux } from '@/core/hooks/useFlux.hook';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { useFluxPaths } from '@/core/hooks/useFluxPaths.hook';
import { Feature } from '@/licenseManagement/licenseManagement.store';
import { CREATED_BY_SEEQ_WORKBENCH, ITEM_TABLE_TYPE, KEY_CODES, SCALING_TABLE_TYPE } from '@/main/app.constants';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { TableTypeEnum } from '@/sdk/model/TableDefinitionOutputV1';
import { DatasourcePreferencesToggle } from '@/search/DatasourcePreferencesToggle.molecule';
import { SearchFilters } from '@/search/SearchFilters.molecule';
import { SearchGroup, type SearchGroupProps } from '@/search/SearchGroup.molecule';
import { SearchResult } from '@/search/SearchResult.molecule';
import { SearchResultIcons } from '@/search/SearchResultIcons.molecule';
import { TrendMultiSelect } from '@/search/multiSelect/TrendMultiSelect.molecule';
import {
  cancellationGroup,
  clear,
  exploreAssetSearchActions,
  fetchHierarchyLayer,
  fetchItemUsages,
  fetchScalingTableSearchItems,
  getStoreForPane,
  initializeSearchActions,
  loadNextPageSearchActions,
  reset,
  setSelectAllItems,
  setUpHierarchiesSearchForItemTable,
} from '@/search/search.actions';
import { HOME_BREADCRUMB } from '@/search/search.constants';
import { getHierarchyItemTrendProps, getScalingTableItemTrendProps } from '@/search/searchResult.utilities.service';
import {
  exploreHierarchyLevel,
  exploreScalingTableLevel,
  filterByAssetItemType,
  filterByName,
  filterItems,
  limitAndFilterItems,
  limitFilterAndSortItems,
  sortByKeyValuesFirst,
} from '@/search/searchWidget.utilities';
import { hasScalingTableCapability } from '@/services/authorization.service';
import { isScalingTableEditorEnabled } from '@/services/systemConfiguration.utilities';
import { getItemTableTypes, getScalingTableTypes } from '@/tableDefinitionEditor/tableDefinition.utilities';
import { doTrack } from '@/track/track.service';
import { addTrendItem } from '@/trendData/trend.actions';
import { API_TYPES_TO_ITEM_TYPES } from '@/trendData/trendData.constants';
import { errorToast } from '@/utilities/toast.utilities';
import {
  isAsset,
  isAssetGroup,
  isDatafile,
  isHierarchy,
  isHierarchyNode,
  isItemTableEditorEnabledForUser,
} from '@/utilities/utilities';
import {
  setIsUsingDatasourcePrefsAssetTrees,
  setUserPreferencesDisplay,
  setUserPreferencesTab,
} from '@/workbench/workbench.actions';
import { PREFERENCE_TABS } from '@/workbench/workbench.store';
import {
  fetchAssetGroups,
  fetchAssets,
  fetchItemTables,
  fetchModels,
  fetchPinnedItems,
  fetchRecentlyAccessedItems,
  fetchScalingTables,
} from '@/workbook/workbook.actions';
import { LOAD_STATUS, WORKBOOK_DISPLAY } from '@/workbook/workbook.constants';
import { Button, Checkbox, Icon, TextField } from '@seeqdev/qomponents';
import type { ItemSearchPreviewV1 } from '@/sdk/model/ItemSearchPreviewV1';
import type { SearchModes, SearchPanes } from '@/search/search.constants';
import type { SearchBreadcrumb, SelectedSearchItem } from '@/search/search.types';

export interface SearchWidgetSharedProps {
  /** @default [sqWorkbookStore.workbookId] */
  scopeIds?: string[];
  searchTypes: string[];
  excludeGloballyScoped?: boolean;
  showPinnedAssets?: boolean;
  showOnlyScopedAssets?: boolean;
  showOnlyConditions?: boolean;
  /** if an override workbookId is provided then we do not use the current workbookId **/
  overrideWorkbookId?: string;
  filterByWorkbook?: boolean;
  selectAllCallback?: (items: SelectedSearchItem[]) => void;
  areAllSelected?: (itemIds: SelectedSearchItem[]) => boolean;
  itemTypeToShowInOverview?: SearchModes;
  overviewLabel?: string;
  showCreateNewItem?: boolean;
  onCreateNewItem?: () => void;
  createNewItemMessage?: string;
  createNewItemLabel?: string;
  doNotDisableOnEdit?: boolean;
}

export interface SearchWidgetProps extends SearchWidgetSharedProps {
  pane: SearchPanes;
  iconClasses?: string;
  restrictExploration?: boolean;
  indicateSelection?: boolean;
  selectedItemId?: string;
  onItemClick?: (item: any, mode?: SearchModes, pane?: SearchPanes) => Promise<any> | void;
  searchResultIcons?: (item: SelectedSearchItem) => Promise<React.ReactNode>;
  showOnlyResults?: boolean;
  allowAssetReplacement: boolean;
  isSelectingAsset?: boolean;
  predefinedSearchTypes?: string[];
  searchResultsClassNames?: string;
  // Used to determine whether or not to clear the modal stores on closing the search widget
  modalId?: string;
  onSelectItem?: (item: any) => void;
  isPanelSearch?: boolean;
  initialItemTable?: SelectedSearchItem;
  initialHierarchy?: SelectedSearchItem;
}

let lastModalId: string | undefined;

export const SearchWidget = (props: SearchWidgetProps) => {
  const {
    pane,
    searchTypes,
    iconClasses,
    scopeIds = [sqWorkbookStore.workbookId],
    excludeGloballyScoped = false,
    onItemClick = _.noop,
    allowAssetReplacement,
    searchResultIcons = defaultSearchResultIcons,
    showOnlyResults = false,
    restrictExploration = false,
    indicateSelection = false,
    selectedItemId = undefined,
    isSelectingAsset = false,
    predefinedSearchTypes,
    modalId = undefined,
    searchResultsClassNames,
    onSelectItem = _.noop,
    isPanelSearch = false,
    selectAllCallback = undefined,
    areAllSelected = () => false,
    showPinnedAssets = false,
    showOnlyScopedAssets = false,
    showOnlyConditions = false,
    overrideWorkbookId = undefined,
    filterByWorkbook,
    itemTypeToShowInOverview,
    overviewLabel = undefined,
    showCreateNewItem = false,
    onCreateNewItem = _.noop,
    createNewItemMessage = '',
    createNewItemLabel = '',
    doNotDisableOnEdit = false,
    initialItemTable = undefined,
    initialHierarchy = undefined,
  } = props;
  if (lastModalId === '') {
    lastModalId = modalId;
  }

  const { t } = useTranslation();
  const assetGroupEditorVisible = useFluxPath(sqLayoutStore, () => sqLayoutStore.assetGroupEditorVisible);
  const scalingTableEditorVisible = useFluxPath(sqLayoutStore, () => sqLayoutStore.tableDefinitionEditorVisible);
  const [nameFilter, setNameFilter] = useState('');
  const [scopedAssetGroups, setScopedAssetGroups] = useState<SelectedSearchItem[]>([]);
  const [scopedAssetTrees, setScopedAssetTrees] = useState<SelectedSearchItem[]>([]);
  const [scalingTableIsCsv, setScalingTableIsCsv] = useState(false);
  const store = getStoreForPane(pane);
  const scrollParentRef = useRef(null);
  const selectedItemIds = useFluxPath(store, () => store.selectedItemIds);
  // NOTE: please do not "simplify" the below to useFlux instead. We're intentionally using useFluxPath to avoid
  // performance hits when typing into the search field.
  const hasNextPage = useFluxPath(store, (store) => store.hasNextPage);
  const mode = useFluxPath(store, (store) => store.mode);
  const searching = useFluxPath(store, (store) => store.searching);
  const isPaginating = useFluxPath(store, (store) => store.isPaginating);
  const currentAsset = useFluxPath(store, (store) => store.currentAsset);
  const selectedItemTable = useFluxPath(store, (store) => store.selectedItemTable);
  const selectedHierarchy = useFluxPath(store, (store) => store.selectedHierarchy);
  const currentHierarchyPath = useFluxPath(store, (store) => store.currentHierarchyPath);
  const selectedScalingTable = useFluxPath(store, (store) => store.selectedScalingTable);
  const selectedScalingTableRow = useFluxPath(store, (store) => store.selectedScalingTableRow);
  const breadcrumbs = useFluxPath(store, (store) => store.breadcrumbs);
  const items = useFluxPath(store, (store) => store.items) ?? [];
  const isAsyncInitialized = useFluxPath(store, (store) => store.isAsyncInitialized);
  const highlightItemId = useFluxPath(store, (store) => store.highlightItemId);
  const hasCreatedScalingTable = useFluxPath(sqWorkbenchStore, () => sqWorkbenchStore.hasCreatedScalingTable);
  const {
    pinned = [],
    recentlyAccessed = [],
    recentlyAccessedLoadStatus,
    pinnedLoadStatus,
    assetGroups = [],
    tableDefinitions = [],
    tableDefinitionsLoadStatus,
    itemTablesLoadStatus,
    itemTables = [],
    assetGroupsLoadStatus,
    workbookId: storeWorkbookId,
    workbookDisplay,
    models = [],
    modelsLoadStatus,
  } = useFlux(sqWorkbookStore);
  const workbookId = overrideWorkbookId ? overrideWorkbookId : storeWorkbookId;
  const { datasources = [] } = useFlux(store);
  const { isUsingDatasourcePrefsAssetTrees } = useFlux(sqWorkbenchStore);
  const filteredDatasources = useFluxPath(sqWorkbenchStore, (s) => s.showOnlyTheseDatasources(datasources));
  // If we get the same number of datasources back, then we didn't filter any datasources out of the full list.
  // That means we can skip any filtering and show all datasources.
  const skipDatasourceCheck = datasources.length === filteredDatasources.length;
  const scalingTableId = useFluxPath(sqScalingTableEditorStore, () => sqScalingTableEditorStore.tableId);
  const itemTableId = useFluxPath(sqScalingTableEditorStore, () => sqItemTableEditorStore.tableId);
  const { itemFinderId, tableDefinitionId } = useFluxPaths(sqTableBuilderStore, ['itemFinderId', 'tableDefinitionId']);

  const getOverviewItems = (overviewItems: SelectedSearchItem[]) => {
    if (isUsingDatasourcePrefsAssetTrees && !skipDatasourceCheck && !itemTypeToShowInOverview) {
      return overviewItems.filter((item) => {
        if (!item?.properties) return false;
        const datasourceIDProperty = item.properties.find((prop) => prop.name === SeeqNames.Properties.DatasourceId);
        return datasourceIDProperty
          ? filteredDatasources.some((ds) => ds.datasourceId === datasourceIDProperty.value)
          : false;
      });
    }
    if (itemTypeToShowInOverview) {
      switch (itemTypeToShowInOverview) {
        case 'pinned':
          return pinned;
        case 'recent':
          return recentlyAccessed;
        case 'asset-groups':
          return assetGroups;
        case 'scaling-tables':
          return tableDefinitions;
        case 'item-tables':
          return itemTables;
        case 'models':
          return models;
      }
    }
    return overviewItems;
  };

  // No need for this to be a state variable as computing it is simple and this ensures it is never stale (CRAB-39094)
  const currentItems: SelectedSearchItem[] = (() => {
    const overviewItems: SelectedSearchItem[] = showOnlyScopedAssets ? scopedAssetTrees : items;
    switch (mode) {
      case 'pinned':
        return pinned;
      case 'recent':
        return recentlyAccessed;
      case 'asset-groups':
        return assetGroups;
      case 'scaling-tables':
        return tableDefinitions;
      case 'item-tables':
        return itemTables;
      case 'overview': // Asset Trees or the item type in itemTypeToShowInOverview, if defined
        return getOverviewItems(overviewItems);
      default:
        return items;
    }
  })();

  const placeholderCategory: string | undefined = (() => {
    switch (mode) {
      case 'tree':
      case 'search':
        return _.get(_.last(_.reject(breadcrumbs, ['type', 'VIEW_MODE'])), 'name');
      case 'pinned':
        return 'SEARCH_DATA.PINNED';
      case 'recent':
        return 'SEARCH_DATA.RECENTLY_ACCESSED';
      case 'asset-groups':
        return 'SEARCH_DATA.ASSET_GROUPS';
      case 'scaling-tables':
        return 'SEARCH_DATA.SCALING';
      case 'item-tables':
        return 'SEARCH_DATA.ITEM_TABLES';
      case 'item-hierarchies':
        return 'SEARCH_DATA.HIERARCHY';
      case 'usages':
        return 'SEARCH_DATA.USAGES';
    }
  })();

  useEffect(() => {
    if (scalingTableId && scalingTableId !== tableDefinitions[length - 1]?.id) {
      fetchScalingTables([workbookId]);
    }
  }, [scalingTableId]);

  useEffect(() => {
    if (itemTableId && itemTableId !== itemTables[length - 1]?.id) {
      fetchItemTables([workbookId]);
    }
  }, [itemTableId]);

  useEffect(() => {
    assetGroupsLoadStatus === LOAD_STATUS.NOT_LOADED && fetchAssetGroups([workbookId]);
    pinnedLoadStatus === LOAD_STATUS.NOT_LOADED && workbookId && fetchPinnedItems(workbookId);
    recentlyAccessedLoadStatus === LOAD_STATUS.NOT_LOADED && workbookId && fetchRecentlyAccessedItems(workbookId);
    tableDefinitionsLoadStatus === LOAD_STATUS.NOT_LOADED && fetchScalingTables([workbookId]);
    modelsLoadStatus === LOAD_STATUS.NOT_LOADED && fetchModels([workbookId]);
    itemTablesLoadStatus === LOAD_STATUS.NOT_LOADED && fetchItemTables([workbookId]);
  }, [
    workbookId,
    assetGroupsLoadStatus,
    pinnedLoadStatus,
    recentlyAccessedLoadStatus,
    tableDefinitionsLoadStatus,
    itemTablesLoadStatus,
    modelsLoadStatus,
  ]);

  const chosenItemTable = mode === 'item-hierarchies' ? selectedItemTable : undefined;

  const isAllUnselectable =
    assetGroupEditorVisible && currentItems.some((item) => item.hasActiveChildren || isAsset(item));

  useEffect(() => {
    if (pane === 'modal' && (modalId !== lastModalId || showOverviewMode)) {
      reset(pane);
      initializeSearchActions(
        pane,
        searchTypes,
        restrictExploration,
        scopeIds,
        excludeGloballyScoped,
        initialItemTable,
        initialHierarchy,
      ).catch((error) => errorToast({ httpResponseOrError: error }));
    } else if (!isAsyncInitialized) {
      // Not ideal to initiate the async fetch from a useEffect, but there are so many modals that open this widget
      // up that this seems better than the alternative of having all the modals call it when the modal opens.
      // Note that this only gets called for modals, stateSynchronizer handles the initialization for the main panel
      // to enable fast follow to work (CRAB-26634)
      initializeSearchActions(
        pane,
        searchTypes,
        restrictExploration,
        scopeIds,
        excludeGloballyScoped,
        initialItemTable,
        initialHierarchy,
      ).catch((error) => errorToast({ httpResponseOrError: error }));
    }

    if (showOnlyScopedAssets) {
      // Get asset groups and asset trees scoped to a provided ID
      fetchAssets(scopeIds, CREATED_BY_SEEQ_WORKBENCH, { cancellationGroup }).then(({ data: { children } }) => {
        setScopedAssetGroups(children ?? []);
      });
      fetchAssets(scopeIds, undefined, { cancellationGroup }).then(({ data: { children } }) => {
        setScopedAssetTrees(children?.filter((item) => item?.scopedTo && scopeIds.includes(item.scopedTo)) ?? []);
      });
    }
    return () => {
      lastModalId = modalId;
    };
  }, []);

  const isDoneLoadingResults = () => !searching && !isPaginating;
  const hasNoResults = () => isDoneLoadingResults() && items.length === 0 && currentItems.length === 0;
  const showErrorInScopedResults = () =>
    showOnlyScopedAssets
      ? isDoneLoadingResults() && scopedAssetGroups.length === 0 && scopedAssetTrees.length === 0 && mode === 'search'
      : true;

  const itemTableSortFunction = sortByKeyValuesFirst<SelectedSearchItem>('id', [itemTableId]);
  const scalingTableSortFunction = sortByKeyValuesFirst<SelectedSearchItem>('id', [scalingTableId]);

  const exploreAsset = (asset: SelectedSearchItem) => {
    return exploreAssetSearchActions(pane, asset.id, searchTypes, restrictExploration, scopeIds, excludeGloballyScoped);
  };

  const isHierarchyNodeModal = itemTypeToShowInOverview === 'hierarchy-nodes' && pane === 'modal';

  const isRestrictedBreadcrumb = (breadcrumb: SearchBreadcrumb) =>
    isHierarchyNodeModal &&
    (breadcrumb.id === HOME_BREADCRUMB.id || breadcrumb.type === SeeqNames.Types.TableDefinition);

  const exploreScalingTable = async (item: SelectedSearchItem) => {
    setNameFilter('');
    return exploreScalingTableLevel(pane, {
      item,
      excludeGloballyScoped,
      scopeIds,
      searchTypes,
      filterByWorkbook,
      setScalingTableIsCsv,
    });
  };

  const exploreHierarchy = async (item: SelectedSearchItem) => {
    setNameFilter('');
    return exploreHierarchyLevel(pane, {
      item,
      excludeGloballyScoped,
      scopeIds,
      searchTypes,
      filterByWorkbook,
    });
  };

  const addHierarchyItemToTrend = async (item: SelectedSearchItem) => {
    const trendItem = {
      itemType: item.node?.itemMetadata ? API_TYPES_TO_ITEM_TYPES[item.node.itemMetadata.type!] : undefined,
      ...item,
    };
    const props = getHierarchyItemTrendProps(item, currentHierarchyPath);
    await addTrendItem(trendItem, props);
  };

  /** used when clicking on items in the search widget */
  const viewHierarchiesLayer = async (item: SelectedSearchItem, isSearch = false) => {
    if (item.type === SeeqNames.Types.TableDefinition) {
      setNameFilter('');
      await setUpHierarchiesSearchForItemTable(pane, item, isSearch);
    } else if (item.type === SeeqNames.Types.Hierarchy) {
      if (!restrictExploration) {
        setNameFilter('');
        await fetchHierarchyLayer(pane, item, item.id, currentHierarchyPath, isSearch);
      }
    } else if (selectedHierarchy && !item.node?.leaf) {
      setNameFilter('');
      await fetchHierarchyLayer(pane, item, selectedHierarchy.id, currentHierarchyPath, isSearch);
    } else if (item.node?.leaf && pane === 'main') {
      // If the node is a leaf, we can just view the item
      await addHierarchyItemToTrend(item);
    }
  };

  const fetchUsages = (item: SelectedSearchItem) => {
    if (item.id) {
      return fetchItemUsages(item, pane, workbookId ? [workbookId] : [], false);
    } else {
      setNameFilter('');
      return exploreAssetSearchActions(
        pane,
        item.id,
        searchTypes,
        restrictExploration,
        undefined,
        excludeGloballyScoped,
      );
    }
  };

  const isScalingTable = (item: any): boolean => {
    return getScalingTableTypes().includes(item.includedProperties?.[SeeqNames.Properties.TableType]?.value);
  };

  const isScalingTableItem = (item: any): boolean => {
    return item.ancestors?.some((ancestor: any) => ancestor.type === SeeqNames.Types.TableDefinition) ?? false;
  };

  const isItemTable = (item: any): boolean => {
    return getItemTableTypes().includes(item.includedProperties?.[SeeqNames.Properties.TableType]?.value);
  };

  const isScalingCsvTable = (item: any) => {
    return item.includedProperties?.[SeeqNames.Properties.TableType]?.value === TableTypeEnum.SCALINGCSV;
  };

  const viewItem = (item: SelectedSearchItem | ItemSearchPreviewV1) => {
    if (isItemTable(item) || isHierarchy(item) || isHierarchyNode(item)) {
      viewHierarchiesLayer(item, false);
    } else if (isScalingTable(item) || isScalingTableItem(item)) {
      viewScalingTableLayer(item, true);
    } else if (isAsset(item) || isDatafile(item) || item.hasActiveChildren) {
      exploreAsset(item);
    } else if (!assetGroupEditorVisible || !scalingTableEditorVisible || pane !== 'main') {
      onItemClick(item, mode, pane);
    }
  };

  const addScalingTableColumnItem = async (item: SelectedSearchItem) => {
    const trendItem = {
      itemType: API_TYPES_TO_ITEM_TYPES[item.type!],
      ...item,
    };
    const props = await getScalingTableItemTrendProps(item);
    await addTrendItem(trendItem, props);
  };

  const viewScalingTableLayer = async (item: SelectedSearchItem, isSearch = false) => {
    if (item.type === SeeqNames.Types.TableDefinition || item.ancestors?.length === 1) {
      setNameFilter('');
      await fetchScalingTableSearchItems(pane, item, item.isScalingTableCSVFlow || isScalingCsvTable(item), isSearch);
    } else {
      let scalingTableIsCSVTable = scalingTableIsCsv;
      if (!scalingTableIsCsv) {
        scalingTableIsCSVTable = isScalingCsvTable(item);
        setScalingTableIsCsv(scalingTableIsCSVTable);
      }
      await addScalingTableColumnItem(item);
    }
  };

  const loadNextPage = () => {
    if (!searching && !isPaginating && hasNextPage) {
      return loadNextPageSearchActions(pane, searchTypes, restrictExploration, scopeIds, excludeGloballyScoped).then(
        ({ items: newItems }) => {
          if (assetGroupEditorVisible || scalingTableEditorVisible) {
            setSelectAllItems(undefined);
          } else {
            const hasLeafItem = !!newItems?.find((item) => !(item.hasActiveChildren || isAsset(item)));
            hasLeafItem && setSelectAllItems(undefined);
          }
        },
      );
    }
  };

  function defaultSearchResultIcons(item: SelectedSearchItem) {
    let onItemClickForDefaultSearchResultIcons = onItemClick;
    if (showScalingTableItems) {
      onItemClickForDefaultSearchResultIcons = addScalingTableColumnItem;
    } else if (showHierarchyNodes) {
      onItemClickForDefaultSearchResultIcons = addHierarchyItemToTrend;
    }
    return Promise.resolve(
      <>
        {allowAssetReplacement && <SearchResultAssetIcons item={item} isAssetGroupView={assetGroupEditorVisible} />}
        <SearchResultIcons
          item={item}
          pinned={pinned}
          workbookDisplay={workbookDisplay}
          workbookId={workbookId}
          onItemClick={onItemClickForDefaultSearchResultIcons}
          allowAssetReplacement={allowAssetReplacement}
          restrictExploration={restrictExploration}
        />
      </>,
    );
  }

  // In pinned, recently accessed or asset group mode, users can filter by the name, pulling this out of the
  // <SearchFilters/> component allows us to not have to rerender every time a user types in an input (CRAB-24095)
  const renderOnlyNameFilter = (
    <div className="card card-default mt5">
      <div className="tw:min-h-0.5 tw:p-4" data-testid="searchPanel">
        <TextField
          extraClassNames="mb10 width-maximum"
          id="searchInput"
          name="nameFilter"
          testId="nameOnlyFilter"
          placeholder={t('SEARCH_DATA.NAME_CONTAINS')}
          onChange={(name) => setNameFilter(name.target.value)}
          value={nameFilter}
        />
        <Button
          label={t('SEARCH_DATA.RESET')}
          onClick={() => {
            clear(
              pane,
              searchTypes,
              restrictExploration,
              scopeIds,
              excludeGloballyScoped,
              initialItemTable,
              initialHierarchy,
            );
            setNameFilter('');
            setScalingTableIsCsv(false);
          }}
          testId="resetButton"
        />
      </div>
    </div>
  );

  const isSearchingTreeBuilderTypesAndNotAtRootOfSearch =
    [SeeqNames.Types.Hierarchy, ITEM_TABLE_TYPE].some((type) => searchTypes.includes(type)) &&
    mode === 'search' &&
    (selectedItemTable || selectedHierarchy);

  const isSearchingScalingTableAndNotAtRootOfSearch =
    searchTypes.includes(SCALING_TABLE_TYPE) && mode === 'search' && (selectedScalingTable || selectedScalingTableRow);
  const showSearchFilters =
    _.includes(['tree', 'overview', 'search'], mode) &&
    !isSearchingTreeBuilderTypesAndNotAtRootOfSearch &&
    !isSearchingScalingTableAndNotAtRootOfSearch;
  const showScalingTableItems = mode === 'scaling-tables-tree';
  const showHierarchyNodes = mode === 'hierarchy-nodes';
  const showItemHierarchies = mode === 'item-hierarchies';
  const showUsages = mode === 'usages';
  const showAssetTrees =
    mode !== 'overview' &&
    !showUsages &&
    !showScalingTableItems &&
    !showItemHierarchies &&
    !showHierarchyNodes &&
    !isSearchingTreeBuilderTypesAndNotAtRootOfSearch &&
    !isSearchingScalingTableAndNotAtRootOfSearch;
  const showOverviewMode = mode === 'overview';
  const showSpinner = !isDoneLoadingResults();
  const showError =
    hasNoResults() && showErrorInScopedResults() && !currentAsset && !showUsages && !showItemHierarchies;
  const showUsagesError = hasNoResults() && showUsages;
  const showPinnedItems = (!showOnlyResults && !showUsages) || showPinnedAssets;
  const showAssetGroups =
    !showUsages && !showScalingTableItems && !showItemHierarchies && !showHierarchyNodes && !itemTypeToShowInOverview;
  const hasScalingLicense = sqLicenseManagementStore.hasValidFeature(Feature.Scaling_Tables);
  const showScalingTables =
    !showUsages &&
    isPanelSearch &&
    (isScalingTableEditorEnabled() || hasScalingTableCapability()) &&
    !showScalingTableItems &&
    (hasScalingLicense || mode === 'overview');
  const showItemTables =
    !showUsages &&
    isPanelSearch &&
    isItemTableEditorEnabledForUser() &&
    !showScalingTableItems &&
    !showItemHierarchies &&
    !showHierarchyNodes;
  const showRecentlyAccessed = !showOnlyResults && !showUsages && !showScalingTableItems;
  const showScalingTableItemSelection = showScalingTableItems && breadcrumbs.length === 2;

  const showInfinite = !_.includes(
    ['pinned', 'recent', 'asset-groups', 'usages', 'scaling-tables', 'item-tables', 'overview', 'item-hierarchies'],
    mode,
  );

  const getScopedAssetTrees = () => scopedAssetTrees.filter((item) => !isAssetGroup(item));
  const showErrorNoResults =
    (isDoneLoadingResults() &&
      !hasNoResults() &&
      currentItems.filter((item) => filterByName(item, mode, nameFilter)).length === 0) ||
    (hasNoResults() && currentAsset) ||
    (getScopedAssetTrees().length === 0 && mode === 'overview' && showOnlyScopedAssets);
  const placeholderCategoryTranslated = placeholderCategory ? t(placeholderCategory) : '';

  const areAllItemsSelected = areAllSelected(currentItems);

  let viewItemForCurrentItems: (item: SelectedSearchItem) => void = viewItem;
  if (showScalingTableItems) {
    viewItemForCurrentItems = viewScalingTableLayer;
  } else if (showItemHierarchies || showHierarchyNodes) {
    viewItemForCurrentItems = viewHierarchiesLayer;
  }
  const currentItemsAsSearchResults = () => {
    const potentiallyFilteredItems =
      showScalingTableItems || showHierarchyNodes
        ? limitAndFilterItems(currentItems, mode, nameFilter, restrictExploration, searchTypes)
        : currentItems;
    return _.map(potentiallyFilteredItems, (item) => (
      <SearchResult
        isSelectingAsset={isSelectingAsset}
        searchTypes={predefinedSearchTypes}
        customIconClasses={iconClasses}
        key={item.id}
        items={currentItems}
        searchResultIcons={searchResultIcons}
        item={item}
        onClickItem={() => viewItemForCurrentItems(item)}
        isSelected={indicateSelection && selectedItemId === item.id}
        itemSelected={_.includes(selectedItemIds, item.id)}
        showItemSelection={!showOverviewMode && pane !== 'modal' && !showScalingTableItemSelection}
        isAllUnselectable={isAllUnselectable}
        highlightItem={highlightItemId === item.id}
        onSelectItem={onSelectItem}
        assetGroupEditorVisible={assetGroupEditorVisible}
        showOnlyConditions={showOnlyConditions}
        doNotDisableOnEdit={doNotDisableOnEdit}
        noNavigate={restrictExploration}
      />
    ));
  };

  return (
    <div className="flexFill flexRowContainer overflowHidden searchWidget height-maximum" data-testid="searchWidget">
      {showSearchFilters ? (
        <SearchFilters
          pane={pane}
          searchTypes={searchTypes}
          scopeIds={scopeIds}
          excludeGloballyScoped={excludeGloballyScoped}
          filterByWorkbook={filterByWorkbook}
        />
      ) : (
        renderOnlyNameFilter
      )}
      {showAssetTrees && (
        <ul
          data-testid="searchBreadcrumbsAssetTrees"
          className="unstyled-list searchBreadcrumbs flexColumnContainer flexWrap flexNoGrowNoShrink mb0 heading">
          {breadcrumbs.map((breadcrumb, index: number) => {
            const isViewMode = breadcrumb.type === 'VIEW_MODE';
            const isLast = breadcrumbs.length - 1 === index;
            return (
              <li
                data-testid="searchBreadcrumbAssetTrees"
                tabIndex={isLast ? undefined : 0}
                onKeyUp={(e) =>
                  e.keyCode === KEY_CODES.ENTER && !isLast && !breadcrumb.isLoading && exploreAsset(breadcrumb)
                }
                onClick={() => !breadcrumb.isLoading && !isLast && exploreAsset(breadcrumb)}
                key={breadcrumb.id}>
                {breadcrumb.isLoading && <IconWithSpinner spinning={true} />}
                {!breadcrumb.id && <Icon icon="fa-house-chimney" extraClassNames="pl5" testId="breadcrumbHomeIcon" />}
                <span className="fs15">{isViewMode ? t(breadcrumb.name) : breadcrumb.name}</span>
                {!breadcrumb.isLoading && breadcrumbs.length === 1 && (
                  <span className="fs15 pl5">{t('SEARCH_DATA.ASSET_TREES')}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {(showScalingTableItems || !!selectedScalingTable || !!selectedScalingTableRow) && breadcrumbs && (
        <ul
          data-testid="searchBreadcrumbsScalingTable"
          className="unstyled-list searchBreadcrumbs flexColumnContainer flexWrap flexNoGrowNoShrink mb0 heading">
          {breadcrumbs.map((breadcrumb, index: number) => {
            const isViewMode = breadcrumb.type === 'VIEW_MODE';
            const isLast = breadcrumbs.length - 1 === index;
            return (
              <li
                data-testid="searchBreadcrumbScalingTable"
                tabIndex={isLast ? undefined : 0}
                onKeyUp={(e) =>
                  e.keyCode === KEY_CODES.ENTER && !isLast && !!breadcrumb.isLoading && exploreScalingTable(breadcrumb)
                }
                onClick={() => !breadcrumb.isLoading && !isLast && exploreScalingTable(breadcrumb)}
                key={breadcrumb.id}>
                {breadcrumb.isLoading && <IconWithSpinner spinning={true} />}
                {!breadcrumb.id && <Icon icon="fa-house-chimney" extraClassNames="pl5" testId="breadcrumbHomeIcon" />}
                <span className="fs15">{isViewMode ? t(breadcrumb.name) : breadcrumb.name}</span>
              </li>
            );
          })}
        </ul>
      )}
      {(showItemHierarchies || showHierarchyNodes || !!selectedHierarchy || !!selectedItemTable) && breadcrumbs && (
        <ul
          data-testid="searchBreadcrumbsItemHierarchies"
          className="unstyled-list searchBreadcrumbs flexColumnContainer flexWrap flexNoGrowNoShrink mb0 heading">
          {breadcrumbs.map((breadcrumb, index: number) => {
            const isViewMode = breadcrumb.type === 'VIEW_MODE';
            const isLast = breadcrumbs.length - 1 === index;
            const isRestricted = isRestrictedBreadcrumb(breadcrumb);
            return (
              <li
                className={clsx(isRestricted && 'restricted')}
                data-testid={`searchBreadcrumbItemHierarchies_${breadcrumb.name}`}
                tabIndex={isLast || isRestricted ? undefined : 0}
                onKeyUp={(e) =>
                  e.keyCode === KEY_CODES.ENTER &&
                  !isLast &&
                  !breadcrumb.isLoading &&
                  !isRestricted &&
                  exploreHierarchy(breadcrumb)
                }
                onClick={() => !breadcrumb.isLoading && !isLast && !isRestricted && exploreHierarchy(breadcrumb)}
                key={breadcrumb.id}>
                {breadcrumb.isLoading && <IconWithSpinner spinning={true} />}
                {!breadcrumb.id && (
                  <Icon
                    icon="fa-house-chimney"
                    extraClassNames="pl5"
                    testId="breadcrumbHomeIcon"
                    type={isRestricted ? 'dark-gray' : undefined}
                  />
                )}
                <span className="fs15">{isViewMode ? t(breadcrumb.name) : breadcrumb.name}</span>
              </li>
            );
          })}
        </ul>
      )}
      {showError && <ErrorWithBackground errorText="SEARCH_DATA.NO_RESULTS" dismissible={false} type="Warning" />}
      {showInfinite &&
      !showOverviewMode &&
      !showErrorNoResults &&
      pane !== 'modal' &&
      !_.isEmpty(currentItems) &&
      !showScalingTableItemSelection ? (
        <div className="pt10 pb10 itemRow pl5">
          <TrendMultiSelect pane={pane} items={currentItems} />
        </div>
      ) : null}
      {showAssetTrees && selectAllCallback !== undefined && !filterByWorkbook && (
        <div className="pt10 pb10 itemRow">
          <Checkbox
            checked={areAllItemsSelected}
            label={t(areAllItemsSelected ? 'UNSELECT_ALL' : 'SELECT_ALL')}
            onClick={() => selectAllCallback(currentItems)}
          />
        </div>
      )}
      <div
        id="spec-search-results"
        data-testid="spec-search-results"
        className={clsx('flexRowContainer overflowAuto height-maximum', searchResultsClassNames)}
        ref={scrollParentRef}>
        {showUsages && (
          <>
            <ul
              data-testid="searchBreadcrumbsUsages"
              className="unstyled-list searchBreadcrumbs flexColumnContainer flexWrap flexNoGrowNoShrink mb0 heading">
              {_.map(breadcrumbs, (breadcrumb, index) => {
                const isTitle = breadcrumb.id === 'ITEM_USAGES';
                const isItemOrHomeBreadcrumb = !(breadcrumbs.length - 1 === _.toNumber(index) || isTitle);
                return (
                  <li
                    data-testid="searchBreadcrumbUsages"
                    tabIndex={!isItemOrHomeBreadcrumb ? undefined : 0}
                    onKeyUp={(e) =>
                      e.keyCode === 13 && isItemOrHomeBreadcrumb && !breadcrumb.isLoading && fetchUsages(breadcrumb)
                    }
                    onClick={() => {
                      !breadcrumb.isLoading && isItemOrHomeBreadcrumb && fetchUsages(breadcrumb);
                    }}
                    key={breadcrumb.id}>
                    {breadcrumb.isLoading && <IconWithSpinner spinning={true} />}
                    {!breadcrumb.id && <Icon icon="fa-house-chimney" testId="usagesHomeIcon" />}
                    {isTitle && <Icon icon="fc-usage" extraClassNames="pl5 pr5 sq-icon-dark-gray " />}
                    <span className={isTitle ? 'text-bold title fs15' : 'fs15'}>{t(breadcrumb.name)} </span>
                  </li>
                );
              })}
            </ul>
            <SearchGroup
              isSelectingAsset={isSelectingAsset}
              predefinedSearchTypes={predefinedSearchTypes}
              loadStatus={searching as SearchGroupProps['loadStatus']}
              label="USAGES"
              searchMode="usages"
              searchTypes={[]}
              scopeIds={scopeIds}
              restrictExploration={restrictExploration}
              pane={pane}
              onErrorClick={_.noop}
              onClickItem={viewItem}
              searchResultIcons={searchResultIcons}
              mode={mode}
              items={limitAndFilterItems(items, mode, nameFilter, restrictExploration, searchTypes)}
              indicateSelection={indicateSelection}
              selectedItemId={selectedItemId}
            />
            {showSpinner && (
              <div className="pt10 pb10 flexNoGrowNoShrink flexColumnContainer flexCenter">
                <IconWithSpinner spinning={true} large={true} />
              </div>
            )}
          </>
        )}

        {showUsagesError && (
          <ErrorWithBackground errorText="SEARCH_DATA.NO_RESULTS" dismissible={false} type="Warning" />
        )}

        {showPinnedItems && (
          <SearchGroup
            isSelectingAsset={isSelectingAsset}
            predefinedSearchTypes={predefinedSearchTypes}
            loadStatus={pinnedLoadStatus}
            label="PINNED"
            pane={pane}
            searchMode="pinned"
            searchTypes={searchTypes}
            scopeIds={scopeIds}
            restrictExploration={restrictExploration}
            onErrorClick={() => fetchPinnedItems(workbookId)}
            onClickItem={(item) => {
              if (showScalingTableItems || isScalingTable(item)) {
                viewScalingTableLayer(item);
              } else if (showItemHierarchies || isItemTable(item) || isHierarchy(item)) {
                viewHierarchiesLayer(item);
              } else {
                viewItem(item);
              }
            }}
            searchResultIcons={searchResultIcons}
            mode={mode}
            items={limitAndFilterItems(
              !showPinnedAssets ? pinned : filterByAssetItemType(pinned),
              mode,
              nameFilter,
              restrictExploration,
              searchTypes,
            )}
            indicateSelection={indicateSelection}
            selectedItemId={selectedItemId}
            totalItemsCount={
              filterItems(
                !showPinnedAssets ? pinned : filterByAssetItemType(pinned),
                mode,
                nameFilter,
                restrictExploration,
                searchTypes,
              ).length
            }
          />
        )}

        {showAssetGroups && (
          <SearchGroup
            isSelectingAsset={isSelectingAsset}
            predefinedSearchTypes={predefinedSearchTypes}
            loadStatus={assetGroupsLoadStatus}
            label="ASSET_GROUPS"
            searchMode="asset-groups"
            searchTypes={searchTypes}
            scopeIds={scopeIds}
            restrictExploration={restrictExploration}
            onErrorClick={() => fetchAssetGroups([workbookId])}
            pane={pane}
            onClickItem={viewItem}
            searchResultIcons={searchResultIcons}
            mode={mode}
            noItemsMessage={
              sqLicenseManagementStore.hasAssetGroups() && sqWorkbookStore.workbookDisplay === WORKBOOK_DISPLAY.EDIT
                ? 'SEARCH_DATA.NO_ASSET_GROUPS'
                : undefined
            }
            items={limitAndFilterItems(
              showOnlyScopedAssets ? scopedAssetGroups : assetGroups,
              mode,
              nameFilter,
              restrictExploration,
              searchTypes,
            )}
            indicateSelection={indicateSelection}
            selectedItemId={selectedItemId}
            totalItemsCount={
              filterItems(
                showOnlyScopedAssets ? scopedAssetGroups : assetGroups,
                mode,
                nameFilter,
                restrictExploration,
                searchTypes,
              ).length
            }
          />
        )}

        {showItemTables && (
          <SearchGroup
            isSelectingAsset={isSelectingAsset}
            predefinedSearchTypes={predefinedSearchTypes}
            loadStatus={itemTablesLoadStatus}
            label="ITEM_TABLES"
            searchMode="item-tables"
            searchTypes={searchTypes}
            scopeIds={scopeIds}
            restrictExploration={restrictExploration}
            onErrorClick={() => fetchItemTables([workbookId])}
            pane={pane}
            onClickItem={async (params) => {
              await viewHierarchiesLayer(params);
            }}
            searchResultIcons={searchResultIcons}
            mode={mode}
            noItemsMessage={
              sqWorkbookStore.workbookDisplay === WORKBOOK_DISPLAY.EDIT ? 'SEARCH_DATA.NO_ITEM_TABLE' : undefined
            }
            items={limitFilterAndSortItems(
              itemTables,
              mode,
              nameFilter,
              restrictExploration,
              searchTypes,
              itemTableSortFunction,
            )}
            indicateSelection={indicateSelection}
            selectedItemId={selectedItemId}
            totalItemsCount={filterItems(itemTables, mode, nameFilter, restrictExploration, searchTypes)?.length}
            showPreviewBadge={true}
          />
        )}

        {showItemHierarchies && chosenItemTable && (
          <SearchGroup
            isSelectingAsset={isSelectingAsset}
            predefinedSearchTypes={predefinedSearchTypes}
            loadStatus={searching as any}
            label="HIERARCHY"
            searchMode="item-hierarchies"
            searchTypes={searchTypes}
            scopeIds={scopeIds}
            restrictExploration={restrictExploration}
            onErrorClick={async () => await viewHierarchiesLayer(chosenItemTable)}
            pane={pane}
            onClickItem={!restrictExploration ? viewHierarchiesLayer : _.noop}
            searchResultIcons={searchResultIcons}
            mode={mode}
            noItemsMessage={
              sqWorkbookStore.workbookDisplay === WORKBOOK_DISPLAY.EDIT ? 'SEARCH_DATA.NO_ITEM_HIERARCHIES' : undefined
            }
            items={limitAndFilterItems(currentItems, mode, nameFilter, restrictExploration, searchTypes)}
            indicateSelection={indicateSelection}
            selectedItemId={selectedItemId}
            totalItemsCount={filterItems(currentItems, mode, nameFilter, restrictExploration, searchTypes)?.length}
          />
        )}

        {showScalingTables && (
          <SearchGroup
            isSelectingAsset={isSelectingAsset}
            predefinedSearchTypes={predefinedSearchTypes}
            loadStatus={tableDefinitionsLoadStatus}
            label="SCALING"
            searchMode="scaling-tables"
            searchTypes={searchTypes}
            scopeIds={scopeIds}
            restrictExploration={restrictExploration}
            onErrorClick={() => fetchScalingTables([workbookId])}
            pane={pane}
            onClickItem={viewScalingTableLayer}
            searchResultIcons={searchResultIcons}
            mode={mode}
            noItemsMessage={
              sqWorkbookStore.workbookDisplay === WORKBOOK_DISPLAY.EDIT ? 'SEARCH_DATA.NO_SCALING' : undefined
            }
            items={limitFilterAndSortItems(
              tableDefinitions,
              mode,
              nameFilter,
              restrictExploration,
              searchTypes,
              scalingTableSortFunction,
            )}
            indicateSelection={indicateSelection}
            selectedItemId={selectedItemId}
            totalItemsCount={filterItems(tableDefinitions, mode, nameFilter, restrictExploration, searchTypes)?.length}
            disableSearchGroup={!hasScalingLicense && mode === 'overview'}
            tooltipLink="https://telemetry.seeq.com/support-link/kb/latest/cloud/scaling-tables-preview"
            onTooltipClicked={() =>
              doTrack('Enterprise Licensing', 'Scaling TableKnowledge Base Clicked', { hasEnterpriseLicense: false })
            }
            showNewBadge={!hasCreatedScalingTable}
          />
        )}

        {showRecentlyAccessed && (
          <SearchGroup
            isSelectingAsset={isSelectingAsset}
            loadStatus={recentlyAccessedLoadStatus}
            label="RECENTLY_ACCESSED"
            searchMode="recent"
            searchTypes={searchTypes}
            scopeIds={scopeIds}
            restrictExploration={restrictExploration}
            pane={pane}
            onErrorClick={() => fetchRecentlyAccessedItems(workbookId)}
            onClickItem={viewItem}
            searchResultIcons={searchResultIcons}
            mode={mode}
            items={limitAndFilterItems(recentlyAccessed, mode, nameFilter, restrictExploration, searchTypes)}
            indicateSelection={indicateSelection}
            selectedItemId={selectedItemId}
            totalItemsCount={filterItems(recentlyAccessed, mode, nameFilter, restrictExploration, searchTypes)?.length}
          />
        )}

        {showOverviewMode && (
          <>
            <div data-testid="overviewHeading" className="flexColumnContainer flexSpaceBetween ml2 mb0 heading">
              <div className="fs15 text-interactive">{t(overviewLabel ?? 'SEARCH_DATA.ASSET_TREES')}</div>
              {!itemTypeToShowInOverview && (
                <DatasourcePreferencesToggle
                  on={isUsingDatasourcePrefsAssetTrees}
                  onChange={() => setIsUsingDatasourcePrefsAssetTrees(!isUsingDatasourcePrefsAssetTrees)}
                  formattedLabel={t('SEARCH_DATA.USE_DATASOURCE_PREFERENCES')}
                  onClick={() => {
                    setUserPreferencesTab(PREFERENCE_TABS.DATASOURCES);
                    setUserPreferencesDisplay(true);
                  }}
                  extraClassNames="mr5"
                />
              )}
            </div>
            <div>{currentItemsAsSearchResults()}</div>
          </>
        )}

        {showInfinite && (
          <>
            <InfiniteScroll
              pageStart={0}
              hasMore={hasNextPage}
              loadMore={loadNextPage}
              useWindow={false}
              initialLoad={false}
              getScrollParent={() => scrollParentRef.current}>
              <>{currentItemsAsSearchResults()}</>
            </InfiniteScroll>

            {showSpinner && (
              <div className="pt10 pb10 flexNoGrowNoShrink flexColumnContainer flexCenter">
                <IconWithSpinner testId="loadingResults" spinning={true} large={true} />
              </div>
            )}
          </>
        )}
      </div>
      {showErrorNoResults && !showUsages && (
        <ErrorWithBackground
          type="Warning"
          dismissible={false}
          errorText={
            showOnlyScopedAssets ? 'SEARCH_DATA.NO_RESULTS_IN_ANALYSIS' : 'SEARCH_DATA.NO_RESULTS_WITHIN_CATEGORY'
          }
          errorParameters={{ category: placeholderCategoryTranslated }}
        />
      )}
      {showCreateNewItem && (
        <div data-testid="createNewItemMessage" className="flexRowContainer flexCenter mt20 mb20">
          <div className="mb10 text-bold">{t(createNewItemMessage)}</div>
          <Button
            testId="createNewItemButton"
            label={t(createNewItemLabel)}
            onClick={onCreateNewItem}
            variant="theme"
          />
        </div>
      )}
    </div>
  );
};
